import { Calendar, Clock, MapPin, Star, Users, UserSound } from '@phosphor-icons/react';
import { EVENT_CATEGORY_COLORS, type SiroccoEvent } from '@sirocco/shared';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { EventAffiche, EventEnLigne, EventEtat, EventSeuil, EventSpontane } from './ui/EventEtat';
import { shortDate, timeRange } from '../utils/format';
import { locale, t } from '../i18n';
import { drapeauxDe, nomsDe } from '../utils/langues';

/**
 * La carte d'une sortie.
 *
 * Elle vivait dans l'onglet Evenements, et les signets en avaient une
 * seconde, plus maigre : meme sortie, deux visages, dont l'un sans son
 * affiche. Retrouver ce qu'on a mis de cote ne devrait pas donner
 * l'impression de tomber sur autre chose.
 *
 * # L'apparition n'est pas de son ressort
 *
 * `data-cascade` rend la tuile transparente jusqu'a ce qu'une vague la
 * fasse paraitre. Une carte qui le porterait dans un ecran sans vague
 * resterait donc invisible pour toujours — d'ou le drapeau, faux par
 * defaut : seul un ecran qui appelle `useCascade` le leve.
 */
export function EventCard({
  event,
  cascade = false,
  compact = false,
  action,
}: {
  event: SiroccoEvent;
  /** L'ecran fait-il paraitre ses tuiles par vagues. */
  cascade?: boolean;
  /**
   * La meme carte, en plus petit.
   *
   * Pour la bulle d'une conversation : une carte a pleine taille y
   * mangerait le fil. Rien n'est retire — l'affiche, les etiquettes et
   * les quatre faits sont ce pour quoi on partage une sortie ; seules les
   * tailles descendent.
   */
  compact?: boolean;
  /** Un geste propre a l'ecran, pose en haut a droite. */
  action?: ReactNode;
}) {
  const color = EVENT_CATEGORY_COLORS[event.category];
  const full = event.participants.length >= event.maxParticipants;
  const { place, ligneDuBas, corps } = usePlaceDesLangues();
  /* Sans le mot : la tete qui parle et les drapeaux suffisent, pour toutes les cartes. */
  const langues = (
    <span className="ev-card__langue">
      <UserSound size={15} />
      <span className="drapeau ev-card__drapeaux" role="img" aria-label={nomsDe(event.langues)}>
        {drapeauxDe(event.langues)}
      </span>
    </span>
  );

  return (
    <Link
      to={`/app/events/${event.id}`}
      {...(cascade ? { 'data-cascade': '' } : {})}
      className={`card ev-card${compact ? ' ev-card--compact' : ''}`}
      style={{ ['--cat' as string]: color }}
    >
      <EventAffiche event={event} />

      <div className="ev-card__corps" ref={corps}>
        <div className="ev-card__head">
          <span className="ev-card__title">{event.title}</span>
          {/*
            La note de l'organisateur : c'est ce qu'on regarde avant de
            s'inscrire chez quelqu'un qu'on ne connait pas. Le libelle lu
            par les lecteurs d'ecran dit de qui elle parle — l'etoile
            seule laisserait croire que c'est l'evenement qui est note.
          */}
          {event.organizerRating && (
            <span
              className="ev-card__note"
              aria-label={t.sorties.carte.note(event.organizerRating.moyenne.toLocaleString(locale))}
            >
              <Star size={13} weight="fill" />
              <span className="ev-card__note-valeur">
                {event.organizerRating.moyenne.toLocaleString(locale)}
              </span>
            </span>
          )}
          {action}
        </div>

        {/* Les etiquettes : ce qu'est la sortie, et ce qu'elle est pour moi. */}
        <div className="ev-card__etiquettes">
          <EventSpontane event={event} />
          <EventEnLigne event={event} />
          <span className="ev-card__category">{t.sorties.categories[event.category]}</span>
          {event.myStatus === 'confirmed' && <span className="ev-card__mine">{t.sorties.carte.inscrit}</span>}
          {event.myStatus === 'pending' && (
            <span className="ev-card__mine ev-card__mine--pending">{t.sorties.carte.enAttente}</span>
          )}
          <EventEtat event={event} />
        </div>

        <div className="ev-card__faits">
          <span className="ev-card__cell">
            <Calendar size={15} />
            <span>{shortDate(event.date)}</span>
          </span>
          <span className="ev-card__cell">
            <Clock size={15} />
            <span>{timeRange(event.startTime, event.endTime)}</span>
          </span>
          {/*
            Les langues vivent au bout de la ligne des participants, sur
            toutes les cartes : a cote du lieu, elles coupaient les
            adresses longues. Voir `usePlaceDesLangues`.
          */}
          {/*
            En ligne, pas de ligne du lieu : elle ne dirait que « En ligne »,
            ce que la pastille au-dessus dit deja. Le lien, s'il y en a un,
            se lit sur la fiche, reserve aux inscrits acceptes.
          */}
          {!event.enLigne && (
            <span className="ev-card__cell ev-card__cell--seul">
              <MapPin size={15} />
              <span>{event.location}</span>
            </span>
          )}
          <span
            ref={ligneDuBas}
            className={`ev-card__cell ev-card__cell--seul${place === 'bas' ? '' : ' ev-card__cell--serree'}`}
          >
            <Users size={15} />
            <span>
              {t.sorties.carte.participants(event.participants.length, event.maxParticipants)}
            </span>
            <EventSeuil event={event} />
            {/* Discret : une mention grise, pas un bandeau rouge. */}
            {full && <span className="ev-card__full">{t.sorties.carte.complet}</span>}
            {place !== 'seule' && langues}
          </span>
          {place === 'seule' && (
            <span className="ev-card__cell ev-card__cell--seul ev-card__cell--langues">{langues}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

/**
 * Les langues au bout de la ligne des participants, et ce qu'on fait
 * quand elle ne tient plus.
 *
 * Si la ligne deborde, elle se resserre : moins d'espace entre ses
 * elements. Et si le nombre de participants se coupe encore, les langues
 * prennent une ligne a elles : ce nombre-la ne se raccourcit pas.
 *
 * La mesure se fait avant que l'ecran ne se peigne, et se refait quand la
 * carte change de largeur ou que la police arrive : on ne voit jamais les
 * langues sauter d'une ligne a l'autre.
 */
function usePlaceDesLangues() {
  const [place, setPlace] = useState<'bas' | 'serree' | 'seule'>('bas');
  const corps = useRef<HTMLDivElement>(null);
  const ligneDuBas = useRef<HTMLSpanElement>(null);

  const deborde = (element: HTMLElement | null) =>
    Boolean(element && element.scrollWidth > element.clientWidth + 1);

  /* La ligne des participants deborde, ou l'un de ses textes s'y coupe. */
  const ligneCoupee = () => {
    const ligne = ligneDuBas.current;
    if (!ligne) return false;
    return (
      deborde(ligne) ||
      [...ligne.children].some(
        (enfant) =>
          enfant instanceof HTMLElement &&
          !enfant.classList.contains('ev-card__langue') &&
          deborde(enfant),
      )
    );
  };

  useLayoutEffect(() => {
    if (place === 'bas' && ligneCoupee()) setPlace('serree');
    else if (place === 'serree' && ligneCoupee()) setPlace('seule');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place]);

  /* Une autre largeur, ou la police enfin chargee : on repart de la ligne des participants. */
  useEffect(() => {
    const element = corps.current;
    if (!element) return;
    let largeur = element.clientWidth;
    const observateur = new ResizeObserver(() => {
      if (element.clientWidth === largeur) return;
      largeur = element.clientWidth;
      setPlace('bas');
    });
    observateur.observe(element);
    let actif = true;
    void document.fonts?.ready.then(() => {
      if (actif) setPlace('bas');
    });
    return () => {
      actif = false;
      observateur.disconnect();
    };
  }, []);

  return { place, corps, ligneDuBas };
}
